import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { createProviderSimRequest } from "../services/apiService";

function NewOrderModal({ isOpen, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [quantity, setQuantity] = useState("");
  const [note, setNote] = useState("");
  const [status, setStatus] = useState({ type: null, message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setQuantity("");
      setNote("");
      setStatus({ type: null, message: "" });
      setIsSubmitting(false);
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape" && !isSubmitting) onClose();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, isSubmitting, onClose]);

  if (!isOpen) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus({ type: null, message: "" });

    const parsedQuantity = Number(quantity);

    if (!quantity.trim() || Number.isNaN(parsedQuantity) || parsedQuantity <= 0) {
      setStatus({ type: "error", message: t("dashboard.newOrder.invalidQuantity") });
      return;
    }

    if (!Number.isInteger(parsedQuantity)) {
      setStatus({ type: "error", message: t("dashboard.newOrder.wholeNumber") });
      return;
    }

    setIsSubmitting(true);

    try {
      await createProviderSimRequest({
        quantity: parsedQuantity,
        note: note.trim() || undefined,
      });
      onSuccess?.();
      onClose();
    } catch (error) {
      setStatus({
        type: "error",
        message:
          error.response?.data?.message ||
          error.message ||
          t("dashboard.newOrder.failed"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="settings-modal-overlay"
      onClick={isSubmitting ? undefined : onClose}
      role="presentation"
    >
      <div
        className="settings-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-order-title"
      >
        <div className="settings-modal__header">
          <h2 id="new-order-title" className="settings-modal__title">
            {t("dashboard.newOrder.title")}
          </h2>
          <button
            type="button"
            className="settings-modal__close"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label={t("dashboard.common.close")}
          >
            &times;
          </button>
        </div>

        <p className="settings-modal__subtitle">{t("dashboard.newOrder.subtitle")}</p>

        <form className="settings-modal__form" onSubmit={handleSubmit}>
          <div className="settings-field">
            <label htmlFor="new-order-quantity">{t("dashboard.newOrder.quantity")}</label>
            <input
              id="new-order-quantity"
              type="number"
              min="1"
              step="1"
              placeholder={t("dashboard.newOrder.quantityPlaceholder")}
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
              disabled={isSubmitting}
            />
          </div>

          <div className="settings-field">
            <label htmlFor="new-order-note">{t("dashboard.newOrder.note")}</label>
            <textarea
              id="new-order-note"
              rows={3}
              placeholder={t("dashboard.newOrder.notePlaceholder")}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              disabled={isSubmitting}
            />
          </div>

          {status.message ? (
            <p
              className={`settings-form-status settings-form-status--${status.type}`}
              role="status"
            >
              {status.message}
            </p>
          ) : null}

          <div className="settings-modal__actions">
            <button
              type="button"
              className="settings-btn settings-btn--outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t("dashboard.common.cancel")}
            </button>
            <button
              type="submit"
              className="settings-btn settings-btn--primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? t("dashboard.common.submitting") : t("dashboard.newOrder.submit")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default NewOrderModal;
